import React, { useState, useEffect, useContext, useRef } from "react";
import { CompareContext, AuthModalContext } from "../App";
import { useUser } from "../context/UserContext";
import CourseCard from "../components/common/CourseCard";
import EnrollModal from "../components/common/EnrollModal";
import { Footer } from "../components/common/SharedSections";
import { fetchCourses } from "../services/api";

const ROWS = [
  {
    label: "Provider",
    key: "provider",
    render: (c) => c.provider?.name || c.provId,
  },
  { label: "Level", key: "level", render: (c) => c.level },
  { label: "Duration", key: "duration", render: (c) => c.duration },
  {
    label: "Rating",
    key: "rating",
    render: (c) => `${c.rating} ★`,
    compare: "max",
  },
  { label: "Students", key: "students", render: (c) => c.students },
  {
    label: "Price",
    key: "price",
    render: (c) => (c.price === 0 ? "N/A" : `₹${c.price?.toLocaleString()}`),
    compare: "min",
  },
  {
    label: "Certificate",
    key: "hasCert",
    render: (c) => (c.hasCert ? "✅ Yes" : "❌ No"),
  },
  {
    label: "Category",
    key: "category",
    render: (c) => c.category?.name || c.catId,
  },
];

const ComparePage = () => {
  const { compareList, toggleCompare, clearCompare } =
    useContext(CompareContext);
  const { isLoggedIn } = useUser();
  const authCtx = useContext(AuthModalContext);
  const [allCourses, setAllCourses] = useState([]);
  const [enrollCourse, setEnrollCourse] = useState(null);
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const handleEnroll = (course) => {
    if (!isLoggedIn) {
      authCtx?.openAuthModal("login");
      return;
    }
    setEnrollCourse(course);
  };

  useEffect(() => {
    fetchCourses({ limit: 50 })
      .then((r) => setAllCourses(r.data.data))
      .catch(() => {});
  }, []);

  const isWinner = (row, course) => {
    if (!row.compare || compareList.length < 2) return false;
    const vals = compareList.map((c) => Number(c[row.key]));
    const target = Number(course[row.key]);
    if (row.compare === "max") return target === Math.max(...vals);
    if (row.compare === "min") return target === Math.min(...vals);
    return false;
  };

  const compareRef = useRef(null);

  useEffect(() => {
    if (compareList.length >= 2 && compareRef.current) {
      const yOffset = -200;
      const y =
        compareRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [compareList]);

  return (
    <div>
      <section style={{ paddingTop: 90 }}>
        <div className="section-tag">Compare Courses</div>
        <div className="section-title">Find Your Best Match</div>
        <p className="section-sub">
          Select up to 3 courses and compare price, duration, rating — winners
          highlighted automatically.
        </p>

        {compareList.length >= 2 && (
          <div
            ref={compareRef}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 12,
              overflow: "hidden",
              marginTop: 20,
            }}
          >
            {/* HEADER */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)`,
                background: "var(--surface2)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ padding: 12, fontWeight: 600 }}>Course</div>

              {compareList.map((course) => (
                <div
                  key={course.id}
                  style={{ padding: 12, textAlign: "center" }}
                >
                  {course.provider?.picLogo ? (
                    <img
                      src={`${SERVER_URL}${course.provider.picLogo}`}
                      alt={course.provider.name}
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: "contain",
                        borderRadius: 8,
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 30 }}>{course.emoji}</span>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 13 }}>
                    {course.title}
                  </div>
                  <button
                    onClick={() => toggleCompare(course)}
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: "2px 8px",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    ✕ Remove
                  </button>
                </div>
              ))}
            </div>

            {/* ROWS */}
            {ROWS.map((row) => (
              <div
                key={row.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)`,
                  borderBottom: "1px solid var(--border)",
                }}
              >
                {/* LABEL */}
                <div
                  style={{
                    padding: 12,
                    background: "var(--surface2)",
                    fontWeight: 500,
                  }}
                >
                  {row.label}
                </div>

                {/* VALUES */}
                {compareList.map((course) => {
                  const winner = isWinner(row, course);

                  return (
                    <div
                      key={course.id}
                      style={{
                        padding: 12,
                        textAlign: "center",
                        background: winner
                          ? "rgba(0,200,100,0.1)"
                          : "transparent",
                        fontWeight: winner ? 600 : 400,
                      }}
                    >
                      {winner && "🏆 "}
                      {row.render(course)}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* ACTION ROW */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)`,
              }}
            >
              <div
                style={{
                  padding: 12,
                  background: "var(--surface2)",
                  fontWeight: 500,
                }}
              >
                Action
              </div>

              {compareList.map((course) => (
                <div key={course.id} style={{ padding: 12 }}>
                  <button
                    onClick={() => handleEnroll(course)}
                    style={{
                      width: "100%",
                      background: "#6c47ff",
                      color: "#fff",
                      border: "none",
                      padding: "10px",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    Enroll →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {compareList.length < 2 && (
          <div className="empty-state" style={{ marginTop: 32 }}>
            <div className="icon">⚖️</div>
            <p>
              Select at least 2 courses from below to compare them side by side.
            </p>
          </div>
        )}

        {compareList.length > 0 && (
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <button className="btn-secondary btn-sm" onClick={clearCompare}>
              Clear All
            </button>
          </div>
        )}

        {/* Pick Courses Grid */}
        <div style={{ marginTop: 48 }}>
          <div className="section-tag">Pick Courses to Compare</div>
          <div className="courses-grid" style={{ marginTop: 16 }}>
            {allCourses.map((c) => (
              <CourseCard key={c.id} course={c} onEnroll={handleEnroll} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      {enrollCourse && (
        <EnrollModal
          course={enrollCourse}
          onClose={() => setEnrollCourse(null)}
        />
      )}
    </div>
  );
};

export default ComparePage;
